import React from 'react'
import classnames from 'classnames'
import { Col, Form, Row } from 'react-bootstrap'
import { type Control, Controller, useWatch } from 'react-hook-form'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { Nullable } from '../../../../common/Nullable'
import { type ComplianceFileType, type ComplianceType, type PhoneComplianceNotice } from '../../../../models/Account'
import { ComplianceNoticeFileForm } from './ComplianceNoticeFile'
import { _5_MB_IN_BYTES } from './complianceNoticeConstants'
import soundWaveIcon from '../../../../assets/images/soundwave-icon.svg?url'
import { type ComplianceNoticeFormValues } from './ComplianceNoticeFormValues'

export interface PhoneComplianceSectionProps {
  control: Control<ComplianceNoticeFormValues>
  togglesDisabled: boolean
  fileActionsEnabled: boolean
  phoneNotice: Nullable<PhoneComplianceNotice> | undefined
  onToggleChange: () => void
  displayFile: (file: Nullable<Blob>, fileType: ComplianceFileType) => void
  updateComplianceFile: (fileId: string, fileType: ComplianceFileType, type: ComplianceType) => void
  resetComplianceFile: (fileType: ComplianceFileType, type: ComplianceType) => void
  isFileLoading: boolean
}

export const PhoneComplianceSection: React.FC<PhoneComplianceSectionProps> = ({
  control,
  togglesDisabled,
  fileActionsEnabled,
  phoneNotice,
  onToggleChange,
  displayFile,
  updateComplianceFile,
  resetComplianceFile,
  isFileLoading,
}) => {
  const [manualAudioEnabled, periodicBeepEnabled] = useWatch({
    control,
    name: ['manualAudioEnabled', 'periodicBeepEnabled'],
  })

  return (
    <>
      <Row
        className={classnames('zocks-mb-2 p-3 gap-3 justify-content-center rounded compliance-component-row', {
          'zocks-border-gray-200': !manualAudioEnabled,
          'zocks-border-primary': manualAudioEnabled,
        })}
      >
        <Col>
          <div className='d-flex flex-row zocks-gap-1.25 zocks-mb-2'>
            <Controller
              name='manualAudioEnabled'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'compliance_notice:update',
                      superScope: ['compliance_notice:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Check
                      aria-label='Manual compliance notice on phone calls'
                      disabled={togglesDisabled}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e)
                        onToggleChange()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
            <div>
              <span className='text-uppercase lead-medium'>Manual compliance notice on phone calls</span>
              <div>Press 5 on the dial pad to play the notice and start Zocks taking notes.</div>
              <div className='zocks-mb-1'>Zocks doesn't take notes until you press 5.</div>
              <div>Upload your own notice up to 5 MB to replace the standard one</div>
              {phoneNotice?.manualAudioFileOriginal ? (
                <div className='paragraph-small zocks-text-gray-600'>Your custom compliance audio will be played.</div>
              ) : (
                <div className='paragraph-small zocks-text-gray-600'>No custom audio uploaded — the default compliance audio will be played.</div>
              )}
            </div>
          </div>
          <ComplianceNoticeFileForm
            uploadEnabled={fileActionsEnabled}
            restoreEnabled={fileActionsEnabled}
            fileType='audio'
            complianceFileInfo={phoneNotice?.manualAudioFileOriginal || null}
            buttonBackgroundImage={soundWaveIcon}
            maxFileSize={_5_MB_IN_BYTES}
            downloadFileCallback={displayFile}
            uploadFileCallback={updateComplianceFile}
            resetFileCallback={resetComplianceFile}
            supportedMimeTypes='audio/wav,audio/mp3'
            isLoading={isFileLoading}
            type='phone'
          />
        </Col>
      </Row>

      <Row
        className={classnames('zocks-mb-2 p-3 gap-3 justify-content-center rounded compliance-component-row', {
          'zocks-border-gray-200': !periodicBeepEnabled,
          'zocks-border-primary': periodicBeepEnabled,
        })}
      >
        <Col>
          <div className='d-flex flex-row zocks-gap-1.25'>
            <Controller
              name='periodicBeepEnabled'
              control={control}
              render={({ field }) => {
                return (
                  <ScopedBlock
                    scope={{
                      scope: 'compliance_notice:update',
                      superScope: ['compliance_notice:admin'],
                    }}
                    missingScopeAction={MissingScopeAction.DISABLE}
                  >
                    <Form.Check
                      aria-label='Phone periodic beep'
                      disabled={togglesDisabled}
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e)
                        onToggleChange()
                      }}
                    />
                  </ScopedBlock>
                )
              }}
            />
            <div>
              <span className='text-uppercase lead-medium'>Periodic beep on phone calls</span>
              <div>A short beep plays every minute for the first 5 minutes after Zocks starts taking notes.</div>
            </div>
          </div>
        </Col>
      </Row>
    </>
  )
}
